import { z } from 'zod';
import { FindingStatusSchema, SeveritySchema } from '../domain/finding.js';
import { CloudEdgeKindSchema, CloudNodeTypeSchema, DiscoverySchema } from './cloud-graph.js';
import { IdSchema, IsoDateTimeSchema } from './common.js';
/**
 * Proof paths (ADR 0009): from a finding a scan observed on the internet, to what lies behind it
 * in the cloud account — where every hop is proved.
 *
 * The first hop is the scan's own evidence: the URL it requested and when. Every hop after it is a
 * resource in the discovered graph, reached by an edge that records the AWS API and the field it
 * was read from. Nothing is inferred and nothing is assumed: when the finding's host does not lead
 * to the account, the path says so and stops, rather than drawing a line it cannot prove.
 *
 * A path is reach, not exploitability. It shows what a request to that host can lead to by
 * configuration, so a medium finding in front of a customer table can outrank a high one in
 * front of nothing — and a person decides what that means.
 */
export const PROOF_PATHS_PER_FINDING = 5;
export const PROOF_ITEMS_MAX = 200;
/** How a hop was reached from the one before it. */
export const ProofViaSchema = z.object({
    edge: CloudEdgeKindSchema,
    api: z.string().max(100),
    field: z.string().max(200),
    detail: z.string().max(300).optional(),
});
export const ProofHopSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('INTERNET') }),
    /** What the scan saw: the request, its scan and when. */
    z.object({
        kind: z.literal('OBSERVED'),
        host: z.string().max(253),
        url: z.string().max(2048),
        method: z.string().max(10).optional(),
        scanId: IdSchema,
        lastSeenAt: IsoDateTimeSchema,
    }),
    z.object({
        kind: z.literal('RESOURCE'),
        nodeId: z.string().max(2048),
        type: CloudNodeTypeSchema,
        name: z.string().max(512),
        public: z.boolean(),
        /** Absent on the entry resource, which the host itself resolves to. */
        via: ProofViaSchema.optional(),
    }),
]);
export const ProofPathSchema = z.object({
    target: z.object({
        nodeId: z.string().max(2048),
        type: CloudNodeTypeSchema,
        name: z.string().max(512),
    }),
    hops: z.array(ProofHopSchema).max(16),
});
/**
 * How far a request to the finding's host reaches, by configuration — worst first.
 *
 * `SECRETS` — a secret. `DATA` — a database, table, cache or bucket. `COMPUTE` — code runs, but no
 * data store is reached. `EDGE_ONLY` — the entry resource and nothing behind it. `NOT_IN_ACCOUNT`
 * — the host does not resolve to the linked account, so nothing behind it can be proved.
 */
export const ReachSchema = z.enum(['SECRETS', 'DATA', 'COMPUTE', 'EDGE_ONLY', 'NOT_IN_ACCOUNT']);
export const FindingProofSchema = z.object({
    finding: z.object({
        id: IdSchema,
        engagementId: IdSchema,
        title: z.string().max(300),
        severity: SeveritySchema,
        status: FindingStatusSchema,
        ruleId: z.string().max(100).optional(),
    }),
    host: z.string().max(253),
    reach: ReachSchema,
    /** The resource the host resolves to; null when it is not in the account. */
    entryNodeId: z.string().max(2048).nullable(),
    paths: z.array(ProofPathSchema).max(PROOF_PATHS_PER_FINDING),
    /** How many data stores and secrets the host leads to in all, not only the paths shown. */
    reachable: z.object({
        secrets: z.number().int().nonnegative(),
        dataStores: z.number().int().nonnegative(),
    }),
    /**
     * The path is closed at its first hop: the finding was proved fixed. When a fix pull request
     * proved it, `proofScanId` is the re-scan that showed it gone.
     */
    closed: z
        .object({
        at: IsoDateTimeSchema.optional(),
        proofScanId: IdSchema.optional(),
    })
        .nullable(),
});
/** GET /v1/applications/{id}/proof-paths */
export const ApplicationProofPathsSchema = z.object({
    /** The discovery the paths were traced through; null when the app has none to trace. */
    discovery: DiscoverySchema.nullable(),
    integrity: z.enum(['verified', 'changed']).nullable(),
    /** Why there is nothing to trace, in words, when that is the case. */
    unavailable: z.string().max(300).nullable(),
    /** Open findings first, the furthest reach first, then severity. */
    items: z.array(FindingProofSchema).max(PROOF_ITEMS_MAX),
    summary: z.object({
        reachSecrets: z.number().int().nonnegative(),
        reachData: z.number().int().nonnegative(),
        notInAccount: z.number().int().nonnegative(),
        closed: z.number().int().nonnegative(),
        truncated: z.boolean(),
    }),
});
//# sourceMappingURL=proof-path.js.map